import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
  HelpTooltip,
} from '~/components';
import { useApi, useConfirmation, useToast } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import React, { useRef } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useAdminSession } from '../contexts';
import { adminPermissions } from './permissions';

const PermissionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export default function AdministratorDrawer({ entry, onSaved, onClose, onDeleted }) {
  const isEdit = !!entry?.id;
  const title = `${isEdit ? 'Edit' : 'New'} Administrator`;
  useDocumentTitle(title);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting }, form] = useForm();
  const api = useApi();
  const toast = useToast();
  const confirmation = useConfirmation();
  const { admin } = useAdminSession();

  const isSelf = isEdit && entry.id === admin?.id;

  function handleClose() {
    if (onClose) onClose();
  }

  const initialValues = {
    email: entry?.email ?? '',
    name: entry?.name ?? '',
    password: '',
    isActive: entry?.isActive ?? true,
    ...Object.fromEntries(adminPermissions.map((p) => [p.key, entry?.permissions?.includes(p.key) ?? false])),
  };

  return (
    <Drawer
      isOpen
      title={title}
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}
      width="50rem">
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();

            const permissions = adminPermissions.map((p) => p.key).filter((key) => values[key]);
            const body = { name: values.name, permissions, isActive: values.isActive };
            if (!isEdit) {
              body.email = values.email;
              body.password = values.password;
            } else if (values.password) {
              body.password = values.password;
            }

            if (isEdit) {
              await api.admin.administrators.update(entry.id, body);
            } else {
              await api.admin.administrators.create(body);
            }

            await onSaved();
            form.save();
            closeDrawer();
            form.done();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>
              Are you sure that you want to delete this administrator?
            </DeleteConfirmation>
          ));
          if (!confirm) return;
          try {
            await api.admin.administrators.delete(entry.id);
            await onDeleted();
            closeDrawer();
          } catch ({ message }) {
            toast.error(message);
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              email: isEdit ? Yup.string() : Yup.string().label('Email').email().max(255).required(),
              name: Yup.string().label('Name').max(255).required(),
              password: isEdit
                ? Yup.string().label('Password').min(8).max(255)
                : Yup.string().label('Password').min(8).max(255).required(),
            })}>
            {(formik) => (
              <Form>
                <Form.Section title="Details">
                  <Form.Control>
                    <Field.Text name="email" placeholder="Email" maxLength={255} disabled={isEdit} />
                  </Form.Control>
                  <Form.Control>
                    <Field.Text name="name" placeholder="Name" maxLength={255} />
                  </Form.Control>
                  <Form.Control>
                    <Field.Text
                      name="password"
                      type="password"
                      placeholder={isEdit ? 'Password (leave blank to keep current)' : 'Password'}
                      maxLength={255}
                    />
                  </Form.Control>
                  <Form.Control>
                    <Field.Checkbox name="isActive" label="Active" disabled={isSelf} />
                  </Form.Control>
                </Form.Section>

                <Form.Section title="Permissions">
                  {adminPermissions.map((permission) => (
                    <Form.Control key={permission.key}>
                      <PermissionLabel>
                        <Field.Checkbox
                          name={permission.key}
                          label={permission.label}
                          disabled={isSelf && permission.key === 'admins'}
                        />
                        <HelpTooltip message={permission.description} placement="right" />
                      </PermissionLabel>
                    </Form.Control>
                  ))}
                </Form.Section>

                {status && <FormMessage.Error>{message}</FormMessage.Error>}

                <Drawer.Actions>
                  {isEdit && !isSelf && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                  <Buttons align="right">
                    <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                    <ActionButton isLoading={isSubmitting} type="submit" onClick={() => formik.submitForm()}>
                      Save &amp; Close
                    </ActionButton>
                  </Buttons>
                </Drawer.Actions>
              </Form>
            )}
          </Formik>
        );
      }}
    </Drawer>
  );
}
